import { InMemoryCache } from "@apollo/client";
import { makeVar } from "@apollo/client";
import { compact } from "lodash";
import uniqBy from "lodash/uniqBy";
import { ReactElement } from "react";
import {
  GetLoggedInUserQuery,
  LoggedInUserOrgFragment,
  UserVideoRecordingPreference,
} from "types/graphql-schema";
import { v4 as uuidv4 } from "uuid";

import { graphqlNone } from "@helpers/constants";
import { getUrl } from "@helpers/helpers";

export const ignoredCacheKeys = [
  "SuggestedArtifactNode",
  "MeetingWithRelevantSectionsNode",
  "SummarizedMeasurements",
];
export const artifactNodeTypes = [
  "BaseArtifactNode",
  "ActionItemArtifactNode",
  "FeedbackArtifactNode",
  "DecisionArtifactNode",
  "GoalArtifactNode",
  "RatingArtifactNode",
  "KPIArtifactNode",
  "DocumentArtifactNode",
  "RecognitionArtifactNode",
];
const assessmentSectionTypes = [
  "AssessmentGroupSectionNode",
  "VirtualAssessmentSectionNode",
];
export type allArtifactTypes =
  | "BaseArtifactNode"
  | "ActionItemArtifactNode"
  | "FeedbackArtifactNode"
  | "DecisionArtifactNode"
  | "GoalArtifactNode"
  | "RatingArtifactNode"
  | "KPIArtifactNode"
  | "DocumentArtifactNode"
  | "RecognitionArtifactNode";

export const urlPreviewCardNodeTypes = [
  "InfoCardInterface",
  "JiraIssueNode",
  "SalesforceOpportunityNode",
  "ClickupTaskNode",
  "GithubIssueNode",
  "HubspotDealNode",
  "YouTrackIssueNode",
  "YouTrackProjectNode",
  "MergeTicketNode",
  "MergeCollectionNode",
  "MergeOpportunityNode",
];

export type CurrentUserType = GetLoggedInUserQuery["me"];
export const isConfirmationOpenVar = makeVar(false);
export const isSidebarOpened = makeVar(false);
export const meetingLeftSidePanelDisplayedVar = makeVar(false);
export const isAdminVar = makeVar(false);
export const isManagerVar = makeVar(false);
export const previousUrlVar = makeVar<null | string>(null);
export const clientUuidVar = makeVar(uuidv4());
export const currentUserVar = makeVar<NonNullable<CurrentUserType>>({
  __typename: "UserNode",
  id: graphqlNone,
  email: "",
  emails: [],
  name: "",
  firstName: "",
  lastName: "",
  favouriteArtifacts: { __typename: "ArtifactConnection", edges: [] },
  organizations: { __typename: "OrganizationNodeConnection", edges: [] },
  paidFeatures: { __typename: "PaidFeaturesNode" },
  directReports: { __typename: "UserNodeConnection", edges: [] },
  managers: { __typename: "UserNodeConnection", edges: [] },
  peers: { __typename: "UserNodeConnection", edges: [] },
  reportingTreeIds: [],
  videoRecordingPreference: UserVideoRecordingPreference.Never,
});
export const currentOrganizationVar = makeVar<LoggedInUserOrgFragment>({
  __typename: "OrganizationNode",
  id: graphqlNone,
  globalAdmins: { __typename: "UserMemberNodeConnection", edges: [] },
  name: "",
  goalLabel: "",
  keyResultLabel: "",
  teamLabel: "",
  orgLabel: "",
  recognitionLabel: "",
  oneononeLabel: "",
  reviewLabel: "",
  expectationLabel: "",
  conversationLabel: "",
  developmentLabel: "",
  competencyLabel: "",
  quarterStartMonth: 1,
  enableMeetingSummarization: false,
  actionItemStates: [],
  featureFlags: { __typename: "OrganizationFeatureFlags" },
  maxCareerTrackRoleLevel: 1,
  mergeIntegrationsMetadata: [],
});
export const currentTiptapJWTVar = makeVar<string>("");

export const appVersionVar = makeVar(null);
export const checkinPopoverGoalIdVar = makeVar<null | number>(null);
export const editorVersionVar = makeVar({
  oldVersion: null,
  newVersion: null,
});

export enum NotificationType {
  success = "success",
  error = "error",
}

type Notication = {
  type?: NotificationType;
  title?: string;
  description?: string | ReactElement;
  timeout?: number | null;
};

/**
 * Trigger a new notification
 * Please use errorNotificationVar or successNotificationVar instead.
 */
export const notificationVar = makeVar<Notication | null>(null);
export const errorNotificationVar = (notification: Notication) =>
  notificationVar({
    type: NotificationType.error,
    ...notification,
  });
export const successNotificationVar = (notification: Notication) =>
  notificationVar({
    type: NotificationType.success,
    ...notification,
  });
export const artifactsVar = makeVar(new Set());
export const usersVar = makeVar(new Set());

/** Cache Policies */
const defaultMerge = (existing: any, incoming: any, options: any) => {
  // no existing data in cache, so we return incoming data
  if (!existing || !existing.edges) {
    return incoming;
  }

  // if we are paginating, then merge the data, otherwise use incoming
  const edges = options.variables?.merge
    ? uniqBy(
        compact(existing.edges.concat(incoming.edges)),
        (edge: any) => edge?.node?.__ref,
      )
    : incoming.edges;
  return {
    ...incoming,
    pageInfo: incoming.pageInfo,
    edges,
  };
};

const canReadArtifactRef = (artifactRef: any, canRead: any, readField: any) => {
  if (!canRead(artifactRef)) {
    return null;
  }
  // if user has not permission, return null
  const canReadPermission = readField("canRead", artifactRef);
  if (canReadPermission && canReadPermission.permission === false) {
    return null;
  }
  return artifactRef;
};

const artifactMerge: {
  keyArgs: string[];
  merge: (existing: any, incoming: any, options: any) => void;
  read: (existing: any, options: any) => void;
} = {
  // merge only when the keyArgs are the same
  keyArgs: [
    // NEVER UNCOMMENT THE LINE BELOW
    // WE USE IT TO REFRESH ITEMS IN AN APOLLO QUERY CACHE
    // BY MERGING THEM INTO THE CACHE DATA
    // "idsToMergeInApolloCache",
    "ids",
    "actionItemAssignee",
    "actionItemIsComplete",
    "actionItemState",
    "actionItemCompletedSincePreviousMeeting",
    "assignedToMembersOfOrganizationId",
    "actionItemAssignedToMembersOfTeam",
    "artifactType",
    "artifactTypes",
    "goalOwners",
    "goalContributors",
    "goalInvolvingUsers",
    "goalScope",
    "goalScopes",
    "goalState",
    "goalStates",
    "goalStatus",
    "isStale",
    "goalVisibility",
    "orderBy",
    "owners",
    "search",
    "teamGoalsOfUser",
    "forUserId",
    "organizationId",
    "feedbackRecipient",
    "feedbackRecipients",
    "feedbackSender",
    "feedbackSenders",
    "feedbackState",
    "feedbackStates",
    "recognitionRecipient",
    "recognitionRecipients",
    "recognitionCoreValue",
    "createdBy",
    "createdByIds",
    "createdInMeetingId",
    "actionItemDueBetweenDates",
    "createdBetweenDates",
    "goalDueBetweenDates",
    "goalsCompletedInTheLastXDays",
    "goalParentIds",
    "recognitionReceivedByMembersOfTeam",
  ],
  merge: (
    existing: any,
    incoming: any,
    { variables }: { args: any; variables: any },
  ) => {
    // no existing data in cache, so we return incoming data
    if (!existing || !existing.edges) {
      return incoming;
    }
    // we merge the data
    const edges = variables?.merge
      ? uniqBy(
          existing.edges
            .concat(incoming.edges)
            .filter(({ node }: { node: any }) => !!node),
          (edge: any) => edge.node.__ref,
        )
      : incoming.edges;
    return {
      ...incoming,
      pageInfo: incoming.pageInfo,
      edges,
    };
  },
  read: (
    existingArtifacts = { edges: [] },
    {
      canRead,
      readField,
    }: { canRead: (...args: any[]) => any; readField: (...args: any[]) => any },
  ) => {
    // we remove any dangling references
    // https://www.apollographql.com/docs/react/caching/garbage-collection/#dangling-references
    const filteredArtifacts =
      existingArtifacts.edges?.filter((edge: any) =>
        canReadArtifactRef(edge.node, canRead, readField),
      ) || [];
    return existingArtifacts
      ? { ...existingArtifacts, edges: filteredArtifacts }
      : { edges: [] };
  },
};

const meetingsMerge = {
  // merge only when the keyArgs are the same
  keyArgs: [
    "meetingGroupId",
    "templateId",
    "fromMeetingId",
    "startDatetime_Gte",
    "startDatetime_Lte",
    "startDatetime_Gt",
    "startDatetime_Lt",
    "endDatetime_Gte",
    "endDatetime_Lte",
    "endDatetime_Gt",
    "endDatetime_Lt",
    "participants",
    "participantCount",
    "status",
    "status_In",
    "ignored",
    "draft",
    "search",
    "isFormalOneonone",
    "minimumDateRange",
  ],
  merge: defaultMerge,
};

const redirectCacheRead =
  (attributeName: any, nodeName: any) =>
  (_: any, { args, toReference }: any) => {
    return toReference({
      __typename: nodeName,
      id: args[`${attributeName}`],
    });
  };

const cache = new InMemoryCache({
  possibleTypes: {
    ArtifactInterface: artifactNodeTypes,
    AssessmentSectionInterface: assessmentSectionTypes,
    AssessmentQuestionInterface: [
      "OverallGoalAssessmentQuestionNode",
      "IndividualGoalAssessmentQuestionNode",
      "OverallRecognitionCoreValueAssessmentQuestionNode",
      "IndividualRecognitionCoreValueAssessmentQuestionNode",
      "ResponsibilityAssessmentQuestionNode",
      "MultiChoiceAssessmentQuestionNode",
      "RangeAssessmentQuestionNode",
      "NpsAssessmentQuestionNode",
      "TextAssessmentQuestionNode",
      "CompetencyAssessmentQuestionNode",
      "CompetencyCriteriaAssessmentQuestionNode",
      "TalentIndicatorAssessmentQuestionNode",
    ],
    AssessmentAnswerInterface: [
      "RangeAssessmentAnswerNode",
      "NpsAssessmentAnswerNode",
      "IndividualGoalAssessmentAnswerNode",
      "OverallGoalAssessmentAnswerNode",
      "ResponsibilityAssessmentAnswerNode",
      "CompetencyAssessmentAnswerNode",
      "CompetencyCriteriaAssessmentAnswerNode",
      "MultiChoiceAssessmentAnswerNode",
      "TextAssessmentAnswerNode",
      "TalentIndicatorAssessmentAnswerNode",
      "OverallRecognitionCoreValueAssessmentAnswerNode",
      "IndividualRecognitionCoreValueAssessmentAnswerNode",
    ],
    SearchUnion: [
      "UserNode",
      "TopicNode",
      "MeetingWithRelevantSectionsNode",
      "CommentNode",
      "BaseArtifactNode",
      "JiraIssueNode",
      "GithubIssueNode",
      "SalesforceOpportunityNode",
      "ClickupTaskNode",
      "HubspotDealNode",
      "YouTrackIssueNode",
    ],
    AssessmentAnswerCalibrationInterface: [
      "RangeAssessmentAnswerCalibrationNode",
      "TalentIndicatorAssessmentAnswerCalibrationNode",
    ],
  },
  typePolicies: {
    Query: {
      fields: {
        feed: {
          keyArgs: false,
          merge(existing, incoming) {
            if (!existing) {
              return incoming;
            }
            return [...existing, ...incoming];
          },
        },
        meeting: {
          keyArgs: ["meetingId", "googleMeetUrl"],
          merge: (existing, incoming, { mergeObjects }) => {
            return mergeObjects(existing, incoming);
          },
          read: redirectCacheRead("meetingId", "MeetingNode"),
        },
        pendingFeedbackRequests: {
          keyArgs: [
            "creatorId",
            "createdByReportsOfUserId",
            "createdBetweenDates",
          ],
          merge: (existing, incoming, { mergeObjects }) => {
            return mergeObjects(existing, incoming);
          },
        },
        meetings: meetingsMerge,
        search: {
          // merge only when the keyArgs are the same
          keyArgs: ["options", "searchTerm"],
          merge: defaultMerge,
        },
        artifacts: artifactMerge,
        goalsForUser: artifactMerge,
        topics: {
          // merge only when the keyArgs are the same
          keyArgs: [
            "hasArtifacts",
            "hasActionItems",
            "hasDecisions",
            "meetingInMeetingGroupId",
          ],
          merge: defaultMerge,
        },
        meetingsByManagerReport: {
          // merge only when the keyArgs are the same
          keyArgs: ["organizationId", "personId", "templateId"],
          merge: defaultMerge,
        },
        topicTemplates: {
          // merge only when the keyArgs are the same
          keyArgs: [
            "adhocTemplates",
            "publicTemplate",
            "globalTemplate",
            "includesRatingId",
            "organizationId",
          ],
          merge: defaultMerge,
        },
        artifact: {
          // this helps query artifact(artifactId: x) to find matching node in cache
          read: function (
            existingRef: any,
            { args, toReference, canRead, readField }: any,
          ) {
            // if artifact has existing ref, we return it if the user can read it
            if (existingRef) {
              return canReadArtifactRef(existingRef, canRead, readField);
            }
            // Otherwise we look in the cache to see if we have a matching artifact already cached.
            const match = artifactNodeTypes
              .map((nodeType) => {
                const ref = toReference({
                  __typename: nodeType,
                  id: args.artifactId,
                });
                return ref;
              })
              .find((ref) => {
                if (!canRead(ref)) return false;
                return canReadArtifactRef(ref, canRead, readField);
              });
            return match;
          },
        },
        user: {
          read: redirectCacheRead("userId", "UserNode"),
        },
        comment: {
          read: redirectCacheRead("commentId", "CommentNode"),
        },
        workflow: {
          read: redirectCacheRead("workflowId", "WorkflowNode"),
        },
        meetingGroup: {
          read: redirectCacheRead("meetingGroupId", "MeetingGroupNode"),
        },
        users: {
          keyArgs: [
            "search",
            "isActive",
            "isActiveOrInvited",
            "excludeIds",
            "organization",
          ],
          merge: defaultMerge,
        },
        detailedCompliancePrograms: {
          keyArgs: [
            "assessmentType",
            "state",
            "search",
            "applicableUser",
            "organizationId",
            "dueDate_Lte",
            "dueDate_Gte",
            "onlyActive",
          ],
          merge: defaultMerge,
        },
        compliancePrograms: {
          keyArgs: [
            "assessmentType",
            "state",
            "search",
            "applicableUser",
            "organizationId",
            "dueDate_Lte",
            "dueDate_Gte",
            "onlyActive",
          ],
          merge: defaultMerge,
        },
        basicGoals: {
          keyArgs: [
            "idsToMergeInApolloCache",
            "orderBy",
            "goalScopes",
            "goalStates",
            "goalOwners",
            "goalTeams",
            "inPathOfGoalId",
            "goalDueBetweenDates",
          ],
          merge: defaultMerge,
        },
        assessmentDeliveries: {
          keyArgs: [
            "state",
            "targetId",
            "creatorId",
            "organizationId",
            "excludedProgramIds",
          ],
          merge: defaultMerge,
        },
        assessmentDelivery: {
          keyArgs: ["organizationId"],
          merge: defaultMerge,
        },
        engagementSurveyDeliveries: {
          keyArgs: ["organizationId"],
          merge: defaultMerge,
        },
        assessments: {
          keyArgs: ["state", "targetId", "responderId", "organizationId"],
          merge: defaultMerge,
        },
        assessmentsOpenForNominations: {
          keyArgs: ["targetId", "complianceProgramId"],
          merge: defaultMerge,
        },
        unmetPerformanceAssessmentCompliancePrograms: {
          keyArgs: ["organizationId"],
          merge: defaultMerge,
        },
        unmetPeerAssessmentCompliancePrograms: {
          keyArgs: ["organizationId"],
          merge: defaultMerge,
        },
        unmetManagerEffectivenessCompliancePrograms: {
          keyArgs: ["organizationId"],
          merge: defaultMerge,
        },
        careerTracks: {
          keyArgs: ["organizationId"],
          merge: defaultMerge,
        },
        chatSessions: {
          keyArgs: [],
          merge: defaultMerge,
        },
      },
    },
    MeetingGroupNode: {
      fields: {
        artifacts: artifactMerge,
        goals: artifactMerge,
      },
    },
    FlowNode: {
      fields: {
        meetings: meetingsMerge,
      },
    },
    CompetencyNode: {
      keyFields: false,
      fields: {
        criteria: {
          merge: false,
        },
      },
    },
    UserNode: {
      merge: (_existing, incoming, { isReference }) => {
        const users = usersVar();
        const id = isReference(incoming)
          ? incoming.__ref
          : cache.identify(incoming);
        users.add(id);
        usersVar(users);
        return incoming;
      },
      fields: {
        organizationsAvailableToJoin: {
          merge(_existing, incoming) {
            return incoming;
          },
        },
        url: {
          read(_, { readField }) {
            if (readField("id"))
              return getUrl({
                userId: readField("id"),
              });
            return null;
          },
        },
        managers: {
          keyArgs: ["search", "isActive", "isActiveOrInvited"],
        },
        directReports: {
          keyArgs: ["search", "isActive", "isActiveOrInvited"],
        },
        activities: {
          keyArgs: ["activityType"],
          merge: defaultMerge,
        },
        ownedGoals: {
          keyArgs: ["goalDueBetweenDates", "artifactType"],
          merge: defaultMerge,
        },
      },
    },
    ComplianceProgramNode: {
      fields: {
        nominationsForUser: {
          keyArgs: ["userId"],
        },
        assessments: {
          keyArgs: [
            "state",
            "targetId",
            "responderId",
            "assessmentType",
            "selfAssessment",
          ],
          merge: defaultMerge,
        },
      },
    },
    FavouritesGroupNode: {
      fields: {
        users: {
          keyArgs: ["search", "isActive", "isActiveOrInvited"],
        },
      },
    },
    ChatSessionNode: {
      fields: {
        messages: {
          keyArgs: ["role"],
          merge(_existing, incoming) {
            return incoming;
          },
        },
      },
    },
    HubspotDealNode: {
      keyFields: ["url"],
    },
    GithubIssueNode: {
      keyFields: ["url"],
    },
    JiraIssueNode: {
      keyFields: ["url"],
    },
    MeetingWithRelevantSectionsNode: {
      keyFields: ["meeting", ["id"]],
      fields: {
        relevantSections: {
          merge(_existing, incoming) {
            return incoming;
          },
        },
      },
    },
    OrganizationNode: {
      fields: {
        members: {
          keyArgs: [
            "hasManager",
            "excludeUserId",
            "excludeUserIds",
            "search",
            "membershipStatus",
            "teamId",
            "id",
            "userId",
            "userIsActive",
          ],
          merge: defaultMerge,
        },
        teams: {
          keyArgs: [],
          merge: defaultMerge,
        },
        recognitionStats: {
          keyArgs: [
            "recipientId",
            "coreValueId",
            "startDate",
            "endDate",
            "recognitionReceivedByMembersOfTeam",
          ],
        },
      },
    },
    SlackNotificationForChannelNode: {
      keyFields: false,
      merge: defaultMerge,
    },
    SummarizedMeasurements: {
      keyFields: ["aggregateMeasurementId"],
    },
    KPINode: {
      fields: {
        summarizedMeasurements: {
          keyArgs: ["summaryPeriod", "summaryMethod"],
          merge: defaultMerge,
        },
      },
    },
    TeamNode: {
      fields: {
        members: {
          keyArgs: false,
          merge: defaultMerge,
        },
        teamGoals: {
          keyArgs: ["goalDueBetweenDates", "artifactType", "goalScope"],
          merge: defaultMerge,
        },
      },
    },
    RatingNode: {
      fields: {
        answers: {
          keyArgs: ["creator"],
          merge: defaultMerge,
        },
      },
    },
    ArtifactInterface: {
      merge: (_existing, incoming, { isReference }) => {
        const artifactSet = artifactsVar();
        const id = isReference(incoming)
          ? incoming.__ref
          : cache.identify(incoming);
        artifactSet.add(id);
        artifactsVar(artifactSet);
        return incoming;
      },
      fields: {
        url: {
          read(_, { readField }) {
            if (readField("id") && readField("artifactType"))
              return getUrl({
                artifactId: readField("id"),
                artifactType: readField("artifactType"),
              });
            return null;
          },
        },
        activities: {
          keyArgs: ["activityType"],
          merge: defaultMerge,
        },
        childGoals: {
          keyArgs: ["goalDueBetweenDates", "goalStates"],
          merge(_existing, incoming) {
            return incoming;
          },
        },
      },
    },
    GoalArtifactNode: {
      fields: {
        keyResults: {
          keyArgs: false,
          merge(_existing, incoming) {
            return incoming;
          },
        },
        owners: {
          keyArgs: ["first"],
          merge(_existing, incoming) {
            return incoming;
          },
        },
        contributors: {
          keyArgs: ["first"],
          merge(_existing, incoming) {
            return incoming;
          },
        },
        childGoals: {
          keyArgs: ["goalDueBetweenDates", "goalStates"],
          merge(_existing, incoming) {
            return incoming;
          },
        },
      },
    },
    MeetingNode: {
      fields: {
        topics: {
          keyArgs: ["meetingId", "hasMeetingId", "meetingGroupId", "today"],
          merge: (existing, incoming, { mergeObjects }) => {
            return mergeObjects(existing, incoming);
          },
        },
        recentlyCompletedAssessmentDeliveries: {
          keyArgs: ["assessmentType"],
          merge: defaultMerge,
        },
        artifacts: artifactMerge,
      },
    },
    TopicNode: {
      fields: {
        clientId: {
          read(existing, { readField }) {
            return existing ?? readField?.("id");
          },
        },
        comments: {
          merge(existing, incoming) {
            if (!existing) {
              return incoming;
            }
            const existingEdges = existing?.edges || [];
            const incomingEdges = incoming?.edges || [];
            const newEdges = uniqBy(
              [...existingEdges, ...incomingEdges],
              ({ node }) => node.__ref,
            );
            return {
              totalCount: newEdges.length,
              edges: newEdges,
            };
          },
        },
        relatedTopics: {
          // merge only when the keyArgs are the same
          keyArgs: [
            "hasArtifacts",
            "hasActionItems",
            "hasDecisions",
            "meetingInMeetingGroupId",
            "actionItemIsComplete",
            "orderBy",
          ],
          merge: defaultMerge,
        },
        artifacts: artifactMerge,
      },
    },
  },
});

export default cache;
